<template>
  <v-container>
    <h1 class="text-h3 text-center my-6">Seu Carrinho</h1>
    
    <v-card v-if="itensCarrinho.length === 0" class="text-center pa-6">
      <v-card-text>
        <v-icon
          color="grey"
          size="64"
          class="mb-4"
        >mdi-cart-off</v-icon>
        <h3 class="text-h5 mb-2">Seu carrinho está vazio</h3>
        <p class="mb-4">Adicione alguns produtos para continuar.</p>
      </v-card-text>
      <v-card-actions class="justify-center">
        <v-btn
          color="primary"
          to="/produtos"
          size="large"
        >Ver produtos</v-btn>
      </v-card-actions>
    </v-card>
    
    <template v-else>
      <v-card class="mb-4">
        <v-data-table
          :headers="headers"
          :items="itensCarrinhoFormatado"
          class="elevation-1"
          hide-default-footer
        >
          <template #[`item.imagem`]="{ item }">
            <v-img
              :src="item.imagem"
              width="80"
              height="60"
              class="rounded"
              contain
            ></v-img>
          </template>
          
          <template #[`item.cor`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar
                size="20"
                class="mr-2"
                :style="{ backgroundColor: item.corHex }"
              ></v-avatar>
              {{ item.cor }}
            </div>
          </template>
          
          <template #[`item.acoes`]="{ item }">
            <v-btn
              color="error"
              icon
              variant="text"
              @click="removerItem(item.index)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
      
      <v-row>
        <v-col cols="12" md="6" offset-md="6">
          <v-card class="pa-4">
            <v-list>
              <v-list-item>
                <v-list-item-title class="d-flex justify-space-between">
                  <span>Subtotal</span>
                  <span>R$ {{ totalCarrinho.toFixed(2) }}</span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item>
                <v-list-item-title class="d-flex justify-space-between">
                  <span>Frete</span>
                  <span>Calculado na próxima etapa</span>
                </v-list-item-title>
                <v-list-item-subtitle class="d-flex justify-end mt-2">
                  <v-btn
                    color="secondary"
                    variant="tonal"
                    size="small"
                    @click="showEnderecoDialog = true"
                  >
                    <v-icon class="mr-1">mdi-map-marker</v-icon>
                    Adicionar endereço
                  </v-btn>
                </v-list-item-subtitle>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-title class="d-flex justify-space-between">
                  <span class="text-h6 font-weight-bold">Total</span>
                  <span class="text-h6 font-weight-bold">R$ {{ totalCarrinho.toFixed(2) }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
            <v-alert
                v-if="!enderecoPreenchido"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-3"
                >
                É necessário adicionar um endereço de entrega para finalizar o pedido.
            </v-alert>
            
            <v-card-actions>
              <v-btn
                color="error"
                variant="text"
                @click="limparCarrinho"
              >
                <v-icon left>mdi-delete-sweep</v-icon>
                Limpar carrinho
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                size="large"
                @click="enviarPedido"
                :loading="processando"
                :disabled="!enderecoPreenchido"
              >
                <v-icon left>mdi-send</v-icon>
                Finalizar pedido
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      
      <v-dialog v-model="dialogPdf" max-width="500">
        <v-card>
          <v-card-title class="text-h5 bg-primary text-white">
            <v-icon class="mr-2">mdi-check-circle</v-icon>
            Pedido finalizado com sucesso!
          </v-card-title>
          <v-card-text class="pa-4 mt-4">
            <v-alert
              type="info"
              variant="tonal"
              border="start"
              class="mb-4"
            >
              Para que seu pedido seja concluído, é necessário enviar o PDF por email e efetuar o pagamento.
            </v-alert>
            
            <v-list density="compact">
              <v-list-subheader>Siga os passos abaixo:</v-list-subheader>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-numeric-1-circle</v-icon>
                </template>
                <v-list-item-title>Baixe o PDF do pedido</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-numeric-2-circle</v-icon>
                </template>
                <v-list-item-title>Envie para <strong>lojamimocria@gmail.com</strong></v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-numeric-3-circle</v-icon>
                </template>
                <v-list-item-title>Aguarde as instruções de pagamento</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-btn
              variant="text"
              color="error"
              @click="dialogPdf = false"
            >
              Fechar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              variant="elevated"
              size="large"
              @click="downloadPdf"
            >
              <v-icon class="mr-2">mdi-download</v-icon>
              Baixar PDF do Pedido
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <!-- Diálogo de endereço -->
      <v-dialog v-model="showEnderecoDialog" max-width="600">
        <v-card>
          <v-card-title class="bg-secondary text-white">
            <v-icon class="mr-2">mdi-map-marker</v-icon>
            Informações de entrega
          </v-card-title>
          <v-card-text class="pa-4 mt-4">
            <v-form ref="enderecoForm" v-model="enderecoFormValid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="endereco.nome"
                    label="Nome completo"
                    variant="outlined"
                    :rules="[v => !!v || 'Nome é obrigatório']"
                  ></v-text-field>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="endereco.logradouro"
                    label="Endereço (Rua, Avenida)"
                    variant="outlined"
                    :rules="[v => !!v || 'Endereço é obrigatório']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="endereco.numero"
                    label="Número"
                    variant="outlined"
                    :rules="[v => !!v || 'Número é obrigatório']"
                  ></v-text-field>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="endereco.complemento"
                    label="Complemento (opcional)"
                    variant="outlined"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="endereco.bairro"
                    label="Bairro"
                    variant="outlined"
                    :rules="[v => !!v || 'Bairro é obrigatório']"
                  ></v-text-field>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="endereco.cidade"
                    label="Cidade"
                    variant="outlined"
                    :rules="[v => !!v || 'Cidade é obrigatória']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="endereco.estado"
                    :items="estadosBrasileiros"
                    label="Estado"
                    variant="outlined"
                    :rules="[v => !!v || 'Estado é obrigatório']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="endereco.cep"
                    label="CEP"
                    variant="outlined"
                    v-mask="'#####-###'"
                    :rules="[v => !!v || 'CEP é obrigatório']"
                  ></v-text-field>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="endereco.telefone"
                    label="Telefone para contato"
                    variant="outlined"
                    v-mask="'(##) #####-####'"
                    :rules="[v => !!v || 'Telefone é obrigatório']"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          
          <v-card-actions class="pa-4">
            <v-btn
              variant="text"
              color="error"
              @click="showEnderecoDialog = false"
            >
              Cancelar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              variant="elevated"
              @click="salvarEndereco"
              :disabled="!enderecoFormValid"
            >
              <v-icon class="mr-1">mdi-content-save</v-icon>
              Salvar endereço
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
      >
        {{ snackbar.text }}
        <template #actions>
          <v-btn
            color="white"
            text
            @click="snackbar.show = false"
          >
            Fechar
          </v-btn>
        </template>
      </v-snackbar>
    </template>
  </v-container>
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
// Importa apenas o jsPDF, sem autotable
import jsPDF from 'jspdf'

export default {
  directives: {
    
  },
  setup() {
    const store = useStore()
    const processando = ref(false)
    const dialogPdf = ref(false)
    const showEnderecoDialog = ref(false)
    const enderecoFormValid = ref(false)
    const enderecoForm = ref(null)
    const snackbar = ref({
      show: false,
      text: '',
      color: 'success'
    })
    
    const endereco = ref({
      nome: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
      telefone: ''
    })
    
    const enderecoPreenchido = computed(() => {
      return endereco.value.nome !== '' && 
             endereco.value.logradouro !== '' && 
             endereco.value.numero !== '' &&
             endereco.value.bairro !== '' &&
             endereco.value.cidade !== '' &&
             endereco.value.estado !== '' &&
             endereco.value.cep !== ''
    })
    
    const estadosBrasileiros = [
      'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 
      'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 
      'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ]
    
    const headers = [
      { title: 'Imagem', align: 'center', key: 'imagem' },
      { title: 'Produto', align: 'start', key: 'nome' },
      { title: 'Cor', align: 'start', key: 'cor' },
      { title: 'Preço Unit.', align: 'end', key: 'precoUnitario' },
      { title: 'Quantidade', align: 'center', key: 'quantidade' },
      { title: 'Tamanho', align: 'center', key: 'tamanho' },
      { title: 'Subtotal', align: 'end', key: 'subtotal' },
      { title: 'Ações', align: 'center', key: 'acoes' }
    ]
    
    const itensCarrinho = computed(() => store.state.carrinho)
    
    const itensCarrinhoFormatado = computed(() => {
      return itensCarrinho.value.map((item, index) => ({
        index,
        imagem: item.produto.imagens[0],
        nome: item.produto.nome,
        cor: item.cor.nome,
        corHex: item.cor.hexadecimal || '#cccccc',
        precoUnitario: `R$ ${item.precoUnitario.toFixed(2)}`,
        quantidade: item.quantidade,
        tamanho: item.tamanho.nome,
        subtotal: `R$ ${(item.quantidade * item.precoUnitario).toFixed(2)}`
      }))
    })
    
    const totalCarrinho = computed(() => {
      return itensCarrinho.value.reduce((total, item) => {
        return total + (item.quantidade * item.precoUnitario)
      }, 0)
    })
    
    const removerItem = (index) => {
      store.commit('removerDoCarrinho', index)
      showSnackbar('Item removido do carrinho', 'info')
    }
    
    const limparCarrinho = () => {
      store.commit('limparCarrinho')
      showSnackbar('Carrinho esvaziado', 'info')
    }
    
    const showSnackbar = (texto, cor) => {
      snackbar.value = {
        show: true,
        text: texto,
        color: cor
      }
    }
    
    const gerarPdf = () => {
      const doc = new jsPDF()
      
      // Adiciona título e data
      const dataAtual = new Date().toLocaleDateString('pt-BR')
      const horaAtual = new Date().toLocaleTimeString('pt-BR')
      
      // Número do pedido (aqui você poderia gerar um número único)
      const numeroPedido = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
      
      // Configuração de margens e posições
      const margemEsquerda = 20
      const y = 20
      let yAtual = y
      
      // Cabeçalho
      doc.setFillColor(66, 66, 66)
      doc.rect(margemEsquerda - 5, yAtual - 5, 170, 12, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(14)
      doc.text('COMPROVANTE DE PEDIDO', margemEsquerda, yAtual)
      
      // Reseta cor do texto
      doc.setTextColor(0, 0, 0)
      yAtual += 20
      
      // Informações do pedido
      doc.setFontSize(11)
      doc.text(`Pedido Nº: ${numeroPedido}`, margemEsquerda, yAtual)
      yAtual += 6
      doc.text(`Data: ${dataAtual} - Hora: ${horaAtual}`, margemEsquerda, yAtual)
      yAtual += 6
      doc.text('Pedido gerado automaticamente pelo sistema', margemEsquerda, yAtual)
      
      // Adiciona informações de endereço se disponíveis
      if (enderecoPreenchido.value) {
        yAtual += 15
        
        // Título da seção de endereço
        doc.setFontSize(12)
        doc.setFont(undefined, 'bold')
        doc.text('ENDEREÇO DE ENTREGA:', margemEsquerda, yAtual)
        doc.setFont(undefined, 'normal')
        
        yAtual += 8
        doc.setFontSize(10)
        
        // Dados do cliente
        doc.text(`Nome: ${endereco.value.nome}`, margemEsquerda, yAtual)
        yAtual += 6
        
        // Endereço formatado
        const enderecoCompleto = `${endereco.value.logradouro}, ${endereco.value.numero}`
        doc.text(`Endereço: ${enderecoCompleto}`, margemEsquerda, yAtual)
        yAtual += 6
        
        // Complemento (se houver)
        if (endereco.value.complemento) {
          doc.text(`Complemento: ${endereco.value.complemento}`, margemEsquerda, yAtual)
          yAtual += 6
        }
        
        // Bairro, cidade e estado
        doc.text(`Bairro: ${endereco.value.bairro}`, margemEsquerda, yAtual)
        yAtual += 6
        doc.text(`${endereco.value.cidade} - ${endereco.value.estado}`, margemEsquerda, yAtual)
        yAtual += 6
        
        // CEP e telefone
        doc.text(`CEP: ${endereco.value.cep}`, margemEsquerda, yAtual)
        yAtual += 6
        doc.text(`Telefone: ${endereco.value.telefone}`, margemEsquerda, yAtual)
        
        yAtual += 10
      } else {
        yAtual += 15
      }
      
      // Linha separadora
      doc.setDrawColor(220, 220, 220)
      doc.line(margemEsquerda - 5, yAtual, 185, yAtual)
      yAtual += 10
      
      // Título da tabela
      doc.setFontSize(12)
      doc.text('Itens do Pedido:', margemEsquerda, yAtual)
      yAtual += 8
      
      // Cabeçalho da tabela
      const colWidths = [65, 25, 25, 25, 15, 25]
      const startX = margemEsquerda
      
      // Desenha cabeçalho da tabela
      doc.setFillColor(240, 240, 240)
      doc.rect(startX - 2, yAtual - 5, 165, 8, 'F')
      
      doc.setFontSize(9)
      doc.text('Produto', startX, yAtual)
      doc.text('Cor', startX + colWidths[0], yAtual)
      doc.text('Tamanho', startX + colWidths[0] + colWidths[1], yAtual)
      doc.text('Preço Unit.', startX + colWidths[0] + colWidths[1] + colWidths[2], yAtual)
      doc.text('Qtd', startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], yAtual)
      doc.text('Subtotal', startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4], yAtual)
      
      yAtual += 8
      
      // Itens da tabela
      doc.setFontSize(9)
      
      // Linhas alternadas da tabela
      let isEven = false
      
      // Desenha linhas da tabela
      itensCarrinho.value.forEach((item) => {
        // Cor de fundo alternada
        if (isEven) {
          doc.setFillColor(245, 245, 245)
          doc.rect(startX - 2, yAtual - 5, 165, 8, 'F')
        }
        isEven = !isEven
        
        // Textos
        doc.text(item.produto.nome.substring(0, 25), startX, yAtual)
        
        // Adiciona um pequeno círculo colorido antes do nome da cor
        const corY = yAtual - 2.5;
        const corX = startX + colWidths[0] - 5;
        const corRadius = 2;
        
        // Desenha um pequeno círculo com a cor do produto
        if (item.cor.hexadecimal) {
          // Converte a cor hexadecimal para RGB
          const hex = item.cor.hexadecimal.replace('#', '');
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          
          // Salva o estado atual
          doc.saveGraphicsState();
          
          // Define a cor de preenchimento
          doc.setFillColor(r, g, b);
          
          // Desenha o círculo
          doc.circle(corX, corY, corRadius, 'F');
          
          // Restaura o estado
          doc.restoreGraphicsState();
        }
        
        // Texto da cor com um pequeno recuo para acomodar o círculo
        doc.text(item.cor.nome, startX + colWidths[0] + 2, yAtual);
        
        doc.text(item.tamanho.nome, startX + colWidths[0] + colWidths[1], yAtual)
        doc.text(`R$ ${item.precoUnitario.toFixed(2)}`, startX + colWidths[0] + colWidths[1] + colWidths[2], yAtual)
        doc.text(item.quantidade.toString(), startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], yAtual)
        doc.text(`R$ ${(item.quantidade * item.precoUnitario).toFixed(2)}`, startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4], yAtual)
        
        yAtual += 8
      })
      
      // Linha separadora
      yAtual += 2
      doc.setDrawColor(180, 180, 180)
      doc.line(margemEsquerda - 5, yAtual, 185, yAtual)
      yAtual += 10
      
      // Total
      doc.setFontSize(12)
      doc.setFont(undefined, 'bold')
      doc.text('Total do Pedido:', 120, yAtual)
      doc.text(`R$ ${totalCarrinho.value.toFixed(2)}`, 175, yAtual, { align: 'right' })
      doc.setFont(undefined, 'normal')
      
      yAtual += 20
      
      // Instruções em um box
      doc.setFillColor(245, 245, 245)
      doc.roundedRect(margemEsquerda - 5, yAtual - 5, 170, 40, 3, 3, 'F')
      
      doc.setFontSize(11)
      doc.setFont(undefined, 'bold')
      doc.text('IMPORTANTE: Para concluir seu pedido', margemEsquerda, yAtual)
      doc.setFont(undefined, 'normal')
      
      yAtual += 10
      doc.setFontSize(10)
      doc.text('1. Envie este PDF para o email: lojamimocria@gmail.com', margemEsquerda, yAtual)
      yAtual += 8
      doc.text('2. Efetue o pagamento conforme instruções que serão enviadas', margemEsquerda, yAtual)
      yAtual += 8
      doc.text('3. Após confirmação do pagamento, seu pedido será processado', margemEsquerda, yAtual)
      
      yAtual += 25
      
      // Agradecimento
      doc.setFontSize(12)
      doc.text('Obrigado por sua compra!', 105, yAtual, { align: 'center' })
      
      return doc
    }
    
    const salvarEndereco = () => {
      if (!enderecoFormValid.value) {
        showSnackbar('Por favor, preencha todos os campos obrigatórios', 'error')
        return
      }
      
      showEnderecoDialog.value = false
      showSnackbar('Endereço salvo com sucesso!', 'success')
    }
    
    const downloadPdf = () => {
      const doc = gerarPdf()
      
      // Gera nome do arquivo com data e número do pedido
      const numeroPedido = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
      const dataFormatada = new Date().toISOString().split('T')[0]
      const nomeArquivo = `pedido-${numeroPedido}-${dataFormatada}.pdf`
      
      doc.save(nomeArquivo)
      
      // Mostra mensagem de sucesso
      showSnackbar('PDF baixado com sucesso! Não esqueça de enviá-lo por email.', 'info')
      
      // Opcional: fechar o diálogo após o download
      // dialogPdf.value = false
      
      // Opcional: limpar o carrinho após o download do PDF
      // limparCarrinho()
    }
    
    const enviarPedido = async () => {
      processando.value = true
      
      // Simulando processamento
      setTimeout(() => {
        processando.value = false
        dialogPdf.value = true
        
        // Mostrar snackbar apenas quando o diálogo aparece
        showSnackbar('Pedido registrado. Baixe o PDF e envie por email para concluir.', 'success')
        
        // Não limpar o carrinho ainda, só depois que o usuário baixar o PDF
      }, 2000)
    }
    
    return {
      headers,
      itensCarrinho,
      itensCarrinhoFormatado,
      totalCarrinho,
      processando,
      dialogPdf,
      snackbar,
      removerItem,
      limparCarrinho,
      enviarPedido,
      downloadPdf,
      showEnderecoDialog,
      endereco,
      enderecoFormValid,
      enderecoForm,
      estadosBrasileiros,
      enderecoPreenchido,
      salvarEndereco
    }
  }
}
</script>

<style>
/* Estilos para os botões do carrinho */
.v-card-actions {
  flex-wrap: wrap;
  padding: 12px !important;
}

/* Media query para dispositivos móveis */
@media (max-width: 600px) {
  .v-card-actions {
    flex-direction: column;
    gap: 12px;
  }
  
  .v-card-actions .v-btn {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  .v-card-actions .v-spacer {
    display: none;
  }
  
  /* Ajuste para o botão primário aparecer primeiro */
  .v-card-actions {
    flex-direction: column-reverse;
  }
  
  /* Reduz o tamanho do texto dos botões para caber melhor */
  .v-card-actions .v-btn {
    font-size: 0.9rem;
  }
  
  /* Garante que os ícones fiquem bem alinhados */
  .v-card-actions .v-btn .v-icon {
    margin-right: 6px;
    font-size: 1.1rem;
  }
}
</style>